import { useState } from "react";
import {
  ArrowLeft,
  FileText,
  MessagesSquare,
  MessageSquareText,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import {
  SMS_PAY_NUMBER,
  SMS_CONFIRMATION_MESSAGE,
  SMS_ACTIVATION_MESSAGE,
  WHATSAPP_SMS_ENROLL_MESSAGE,
  WHATSAPP_SMS_ENROLL_URL,
} from "@/config/site";

const API = ""; // same-origin — the ingress/worker proxies /api to the backend

const Bubble = ({ member, testid, children }) => (
  <div
    data-testid={testid}
    className={
      member
        ? "ml-auto max-w-[85%] rounded-xl rounded-br-sm bg-amber-500 px-4 py-3 text-sm font-medium text-stone-950"
        : "max-w-[85%] rounded-xl rounded-bl-sm border border-stone-800 bg-stone-900 px-4 py-3 text-sm leading-relaxed text-stone-200"
    }
  >
    {children}
  </div>
);

const SmsProgram = () => {
  const [form, setForm] = useState({ name: "", phone: "", consent: false });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setResult(null);
    setError("");
    try {
      const res = await fetch(`${API}/api/sms/opt-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ member_name: form.name, phone: form.phone, consent: form.consent }),
      });
      if (!res.ok) throw new Error("bad request");
      setResult(await res.json());
    } catch {
      setError("Enrollment failed — check the number and confirm the consent box.");
    }
  };

  return (
    <div data-testid="sms-program-page" className="min-h-screen bg-stone-950 text-stone-200">
      <div className="max-w-3xl mx-auto px-6 py-16 space-y-12">
        <div className="space-y-4">
          <a href="/" data-testid="sms-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 hover:text-amber-500 transition-colors">
            <ArrowLeft size={13} /> Return to the Institute
          </a>
          <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
            SMS Ledger Program
          </span>
          <h1 className="text-4xl font-bold tracking-tight text-stone-100 display-serif">
            Text-to-Pay Enrollment
          </h1>
          <p className="text-stone-400 text-sm leading-relaxed">
            Enrolled members move 100% cash-backed $sUSD settlement balances by ordinary text
            message. Send <span className="text-stone-200 font-semibold">SEND [amount] TO [member key]</span>{" "}
            to <span className="text-stone-200 font-semibold">{SMS_PAY_NUMBER}</span> and the private
            ledger settles instantly, texting your confirmation back.
          </p>
        </div>

        <div className="rounded-xl border border-amber-500/40 bg-stone-900/40 p-8 space-y-4">
          <div className="flex items-center gap-3">
            <MessageSquareText className="text-amber-500" size={20} />
            <h2 className="text-xl font-bold text-stone-100 display-serif">Option 1 — Join by Text</h2>
          </div>
          <p className="text-sm text-stone-400 leading-relaxed">
            From your mobile phone, text <span className="text-stone-200 font-semibold">JOIN</span> to{" "}
            <span className="text-stone-200 font-semibold">{SMS_PAY_NUMBER}</span>. We text back one
            confirmation request — reply <span className="text-stone-200 font-semibold">YES</span> to
            confirm you agree to receive ledger alerts via SMS, and your device is activated.
            Thereafter you receive only transactional payment confirmations for transfers you initiate.
          </p>
        </div>

        <div className="rounded-xl border border-stone-800 bg-stone-900/20 p-8 space-y-5">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-amber-500" size={20} />
            <h2 className="text-xl font-bold text-stone-100 display-serif">Option 2 — Enroll Here</h2>
          </div>
          <form onSubmit={submit} data-testid="sms-optin-form" className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="optin-name" className="mono-tag text-xs uppercase tracking-widest text-stone-500 block">
                Member Name
              </label>
              <input
                id="optin-name"
                data-testid="optin-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-stone-950 border border-stone-700 rounded-lg px-4 py-3 text-sm text-stone-200 focus:outline-none focus:border-amber-500/60"
                required
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="optin-phone" className="mono-tag text-xs uppercase tracking-widest text-stone-500 block">
                Mobile Number (US)
              </label>
              <input
                id="optin-phone"
                data-testid="optin-phone-input"
                type="tel"
                placeholder="775 555 0100"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-stone-950 border border-stone-700 rounded-lg px-4 py-3 text-sm text-stone-200 focus:outline-none focus:border-amber-500/60"
                required
              />
            </div>
            <label className="flex items-start gap-3 text-xs text-stone-400 leading-relaxed cursor-pointer">
              <input
                type="checkbox"
                data-testid="optin-consent-checkbox"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                className="mt-0.5 accent-amber-500"
                required
              />
              I agree to receive transactional SMS payment confirmations from BGNZN Institute at the
              number provided. Message frequency varies. Msg &amp; data rates may apply. Reply STOP
              to cancel, HELP for help. Consent is not a condition of membership.
            </label>
            <button
              type="submit"
              data-testid="optin-submit-button"
              className="rounded-lg bg-amber-500 px-6 py-3 text-sm font-bold uppercase tracking-wider text-stone-950 hover:bg-amber-400 transition-colors"
            >
              Enroll in Text Alerts
            </button>
          </form>
          {result && (
            <div
              data-testid="optin-confirmation-prompt"
              className="rounded-lg border border-amber-500/50 bg-stone-950/80 p-5 space-y-3"
            >
              <p className="mono-tag text-xs uppercase tracking-widest text-amber-500">
                Confirmation required — check your phone
              </p>
              <p className="text-sm text-stone-300 leading-relaxed">
                We just sent this confirmation request to{" "}
                <span className="text-stone-100 font-semibold">{result.phone}</span>:
              </p>
              <p className="rounded-md border border-stone-800 bg-stone-900 p-4 text-sm leading-relaxed text-stone-200">
                {SMS_CONFIRMATION_MESSAGE}
              </p>
              <p className="text-sm text-stone-400 leading-relaxed">
                Open your messages and reply{" "}
                <span className="text-stone-100 font-semibold">YES</span> to activate ledger alerts.
                Until you confirm, this number cannot send or receive payments.
              </p>
            </div>
          )}
          {error && (
            <p data-testid="optin-error" className="text-sm text-red-400">
              {error}
            </p>
          )}
        </div>

        <div className="rounded-xl border border-stone-800 bg-stone-900/20 p-8 space-y-5">
          <div className="flex items-center gap-3">
            <MessagesSquare className="text-amber-500" size={20} />
            <h2 className="text-xl font-bold text-stone-100 display-serif">Option 3 — Enroll via WhatsApp</h2>
          </div>
          <p className="text-sm text-stone-400 leading-relaxed">
            Membership is admitted through the Directorate Council on WhatsApp. To enroll your
            mobile number in ledger alerts during admissions, send the council the enrollment
            request below — it carries your explicit consent statement:
          </p>
          <p
            data-testid="whatsapp-consent-script"
            className="rounded-md border border-stone-800 bg-stone-950 p-4 text-sm leading-relaxed text-stone-200 italic"
          >
            &ldquo;{WHATSAPP_SMS_ENROLL_MESSAGE}&rdquo;
          </p>
          <a
            href={WHATSAPP_SMS_ENROLL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-sms-enroll-button"
            className="inline-flex items-center justify-center rounded-lg bg-amber-500 px-6 py-3 text-sm font-bold uppercase tracking-wider text-stone-950 hover:bg-amber-400 transition-colors"
          >
            Send Enrollment Request on WhatsApp
          </a>
          <p className="text-xs text-stone-500 leading-relaxed">
            The button opens WhatsApp with this consent statement pre-written. After your request,
            the council sends the standard SMS confirmation request to your mobile number — reply
            YES by text to activate. Members may also sign the written SMS Consent Addendum (below)
            at induction.
          </p>
        </div>

        <div className="rounded-xl border border-stone-800 bg-stone-900/20 p-8 space-y-6">
          <div className="space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              Double Opt-In
            </span>
            <h2 className="text-xl font-bold text-stone-100 display-serif">
              Every Enrollment Confirms by Text
            </h2>
            <p className="text-sm text-stone-400 leading-relaxed">
              Whether a member enrolls by texting JOIN, through the form above, via WhatsApp, or by
              signed written consent, activation always completes through this exact confirmation
              exchange. No number can send or receive payments until its owner replies YES.
            </p>
          </div>
          <div data-testid="optin-transcript" className="space-y-3">
            <Bubble member testid="transcript-join">JOIN</Bubble>
            <Bubble testid="transcript-confirmation-request">{SMS_CONFIRMATION_MESSAGE}</Bubble>
            <Bubble member testid="transcript-yes">YES</Bubble>
            <Bubble testid="transcript-activated">{SMS_ACTIVATION_MESSAGE}</Bubble>
          </div>
          <p className="text-xs text-stone-500 leading-relaxed">
            Reply STOP at any time to unsubscribe — &ldquo;BGNZN Institute: you have been
            unsubscribed from ledger SMS alerts. No further messages will be sent. Reply START to
            re-subscribe.&rdquo; Reply HELP for program support.
          </p>
        </div>

        <div className="rounded-xl border border-stone-800 bg-stone-900/20 p-8 space-y-5">
          <div className="flex items-center gap-3">
            <FileText className="text-amber-500" size={20} />
            <h2 className="text-xl font-bold text-stone-100 display-serif">Membership Documents</h2>
          </div>
          <p className="text-sm text-stone-400 leading-relaxed">
            Every member signs the Cooperative Membership Agreement at induction. Members enrolling
            in ledger alerts also sign the SMS Communications Consent Addendum, which records
            express written consent with the member&rsquo;s name, mobile number, signature, and
            date.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="/assets/membership-agreement.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="membership-agreement-link"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-stone-700 text-stone-300 hover:border-amber-600/60 hover:text-amber-300 text-sm font-semibold transition-colors"
            >
              Cooperative Membership Agreement (PDF)
            </a>
            <a
              href="/assets/sms-consent-addendum.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sms-consent-addendum-link"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-amber-500/60 text-amber-400 hover:bg-amber-500/10 text-sm font-semibold transition-colors"
            >
              SMS Communications Consent Addendum (PDF)
            </a>
          </div>
        </div>

        <div className="space-y-3 text-xs text-stone-500 leading-relaxed border-t border-stone-900 pt-6">
          <p className="flex items-center gap-2">
            <Smartphone size={13} className="text-stone-600 shrink-0" />
            Program: BGNZN Institute ledger alerts. Message frequency varies with your activity.
            Message and data rates may apply. Reply <span className="text-stone-300">STOP</span> to
            opt out at any time, <span className="text-stone-300">HELP</span> for help,{" "}
            <span className="text-stone-300">START</span> to re-subscribe. Supported on major US
            carriers; carriers are not liable for delayed or undelivered messages.
          </p>
          <p>
            Governed by our{" "}
            <a href="/privacy" data-testid="sms-privacy-link" className="text-amber-500 hover:text-amber-400">
              Privacy Policy
            </a>{" "}
            and{" "}
            <a href="/terms" data-testid="sms-terms-link" className="text-amber-500 hover:text-amber-400">
              Terms &amp; Conditions
            </a>
            . No mobile information is shared with third parties or affiliates for marketing purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SmsProgram;
